import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminLaunchReadiness } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#0F7A3E";
const AMBER = "#B4801A";
const RED = "#B4241E";

const dot = { green: GREEN, amber: AMBER, red: RED };
const label = { green: "READY", amber: "PARTIAL", red: "BLOCKED" };

const CheckRow = ({ row }) => (
  <div
    data-testid={`readiness-row-${row.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
    style={{
      display: "grid",
      gridTemplateColumns: "16px 1fr auto",
      gap: 14,
      alignItems: "start",
      padding: "16px 18px",
      borderBottom: "1px solid rgba(10,22,40,0.08)",
      background: "#fff",
    }}
  >
    <div
      aria-hidden
      style={{
        width: 14,
        height: 14,
        borderRadius: 999,
        marginTop: 4,
        background: dot[row.status] || "#999",
        boxShadow: `0 0 0 3px ${dot[row.status]}22`,
      }}
    />
    <div>
      <div style={{ fontWeight: 700, color: NAVY, fontSize: 17 }}>{row.name}</div>
      <div style={{ fontSize: 14, color: "#3A4859", marginTop: 4 }}>{row.detail}</div>
      {row.fix && (
        <div
          style={{
            fontSize: 13,
            color: NAVY,
            marginTop: 8,
            padding: "8px 10px",
            background: "#FFF9E6",
            border: `1px dashed ${GOLD}`,
            borderRadius: 6,
          }}
        >
          <span style={{ fontWeight: 700 }}>FIX → </span>
          {row.fix}
        </div>
      )}
    </div>
    <div
      style={{
        alignSelf: "center",
        fontFamily: "'Bebas Neue', sans-serif",
        letterSpacing: 1,
        fontSize: 13,
        color: dot[row.status],
        border: `1.5px solid ${dot[row.status]}`,
        padding: "4px 10px",
        borderRadius: 4,
        whiteSpace: "nowrap",
      }}
    >
      {label[row.status]}
    </div>
  </div>
);

export default function AdminLaunchReadinessPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await adminLaunchReadiness();
      setData(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load readiness.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div
      data-testid="admin-launch-readiness-page"
      style={{ padding: "28px 22px", maxWidth: 980, margin: "0 auto" }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 6 }}>
        <Link
          to="/admin"
          style={{ fontSize: 13, color: NAVY, textDecoration: "underline" }}
          data-testid="back-to-admin"
        >
          ← ADMIN
        </Link>
      </div>
      <h1
        className="font-display"
        style={{ color: NAVY, fontSize: 44, margin: "0 0 6px", lineHeight: 1 }}
      >
        LAUNCH READINESS
      </h1>
      <p style={{ color: "#3A4859", fontSize: 16, marginTop: 0, maxWidth: 720 }}>
        Every green light needs to be lit before you turn on ad spend. Red rows are hard blockers;
        amber rows can launch but limit revenue.
      </p>

      <div style={{ display: "flex", gap: 10, margin: "16px 0 20px" }}>
        <button
          onClick={load}
          disabled={loading}
          data-testid="refresh-readiness-btn"
          style={{
            background: NAVY,
            color: GOLD,
            padding: "12px 20px",
            border: "none",
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: 1,
            cursor: loading ? "wait" : "pointer",
            minHeight: 44,
          }}
        >
          {loading ? "CHECKING…" : "↻ RE-RUN CHECKS"}
        </button>
      </div>

      {err && (
        <div
          data-testid="readiness-error"
          style={{
            background: "#FDECEA",
            border: `1px solid ${RED}`,
            color: RED,
            padding: 14,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {err}
        </div>
      )}

      {data && (
        <>
          <div
            data-testid="readiness-summary"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              marginBottom: 22,
            }}
          >
            <SummaryCard
              tone={data.ready_to_spend ? "green" : "red"}
              label={data.ready_to_spend ? "CLEAR TO SPEND" : "NOT READY"}
              value={data.ready_to_spend ? "✓" : "✗"}
              sub={
                data.ready_to_spend
                  ? "All hard blockers cleared."
                  : `${data.reds} red · ${data.ambers} amber`
              }
            />
            <SummaryCard tone="green" label="READY" value={data.greens} sub="passing checks" />
            <SummaryCard tone="amber" label="PARTIAL" value={data.ambers} sub="soft warnings" />
            <SummaryCard tone="red" label="BLOCKED" value={data.reds} sub="hard blockers" />
          </div>

          <div
            style={{
              background: "#fff",
              border: "1px solid rgba(10,22,40,0.10)",
              borderRadius: 10,
              overflow: "hidden",
              boxShadow: "0 2px 10px rgba(10,22,40,0.06)",
            }}
          >
            {data.checks.map((row, i) => (
              <CheckRow key={i} row={row} />
            ))}
          </div>

          <p style={{ color: "#5A6674", fontSize: 13, marginTop: 18 }}>
            Sources: Retell <code>list-agents</code> + <code>list-phone-numbers</code>, Meta Graph
            v20 <code>/me</code>, Stripe <code>Account.retrieve</code>, SendGrid{" "}
            <code>/v3/user/credits</code>, live DB counts. Nothing here is hardcoded — every row
            reflects the current environment.
          </p>
        </>
      )}
    </div>
  );
}

const SummaryCard = ({ tone, label, value, sub }) => (
  <div
    style={{
      background: tone === "green" ? "#E7F4EC" : tone === "amber" ? "#FFF4DC" : "#FDECEA",
      border: `1.5px solid ${dot[tone] || "#999"}`,
      borderRadius: 10,
      padding: "14px 16px",
    }}
  >
    <div
      className="font-display"
      style={{ letterSpacing: 1.2, fontSize: 13, color: dot[tone] }}
    >
      {label}
    </div>
    <div style={{ fontSize: 34, fontWeight: 800, color: NAVY, lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 12, color: "#3A4859", marginTop: 2 }}>{sub}</div>
  </div>
);
